import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  ClickAwayListener,
  FormControlLabel,
  Grid,
  IconButton,
  Popper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import { useStationRouteContext } from "Helpers/stationRoutes";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Station } from "assets/SideBarIcons/station.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { GetStationByIdResponse } from "subModule/src/services/station/interface";
import Loader from "layout/Loader";
import DynamicSelect from "common/Select/Select";
import { StationProfileService } from "subModule/src/core/services";
import { useSelector } from "react-redux";
import { selectUserInfo } from "features/user/userSlice";
import { useGetPriceListDropdownService } from "subModule/src/services/pricelist/usePriceList";
import { useGetBusinessGroupDropdownService } from "subModule/src/services/businessgroup/useBusinessGroup";
import { useGetBusinessService } from "subModule/src/services/business/useBusiness";
import COUNTRIES from "common/Others/Countries";
import StationMedia from "./StationMedia";
import StationTabs from "./StationTabs";
import FormDialog from "common/Modal/Modal";
import { fetchMediaAsDataUrl } from "Helpers/mediaView";
import MediaImage from "common/MediaImage/MediaImage";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CommonCheckbox from "common/CheckBox/Checkbox";
import { Add } from "@mui/icons-material";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
import { pickStationDisplayName } from "Helpers/pickProfileEntityTitle";
// import StationSubscriptionPlanStep from "pages/Business/StationSubscriptionPlanStep";
interface FormData {
  [key: string]: any;
}
interface StationModalProps {
  embedded?: boolean;
  embeddedBusinessId?: number | null;
  onEmbeddedComplete?: (station?: {
    id: number;
    name: string;
    businessId?: number;
  }) => void;
  onEmbeddedBack?: () => void;
}

export default function StationModal({
  embedded = false,
  embeddedBusinessId,
  onEmbeddedComplete,
  onEmbeddedBack,
}: StationModalProps = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const {
    stationId,
    businessId: routeBusinessId,
    listPath,
    isBusinessContext,
  } = useStationRouteContext();
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: isBusinessContext ? "businessSetup" : "stations",
    moduleLabelKey: isBusinessContext ? "stations" : "stationsList",
    moduleHref: listPath || "/stations",
    entityFallbackLabelKey: "name",
    isEdit: !!stationId,
  });
  const isBusinessCreateWizard = isBusinessContext && !stationId && !embedded;
  // /station/create has no business in the URL — require selecting one
  const needsBusinessPicker =
    !stationId && !routeBusinessId && !embedded && !embeddedBusinessId;
  const [savedStation, setSavedStation] = useState<{
    id: number;
    name: string;
    businessId?: number;
  } | null>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    reset,
    setValue,
    getValues,
    watch,
    trigger,
  } = useForm<FormData>({
    defaultValues: {
      sellBelowStock: false,
      hours: {
        Monday: [{ open: "09:00", close: "17:00" }],
        Tuesday: [{ open: "09:00", close: "17:00" }],
        Wednesday: [{ open: "09:00", close: "17:00" }],
        Thursday: [{ open: "09:00", close: "17:00" }],
        Friday: [{ open: "09:00", close: "17:00" }],
        Saturday: [{ open: "09:00", close: "17:00" }],
        Sunday: [{ closed: true }],
      },
      vehicleRequiredField: {
        plateNumber: true,
        makeId: true,
        modelId: true,
        vehicleTypeId: true,
      },
    },
  });
  const [loading, setLoading] = useState<boolean>(stationId ? true : false);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    stationId ? false : true,
  );
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const [openDay, setOpenDay] = useState<string | null>(null);
  const loggedInUserData = useSelector(selectUserInfo);
  const selectedBusinessOption = watch("businessId");
  const selectedBusinessId = selectedBusinessOption?.value
    ? Number(selectedBusinessOption.value)
    : undefined;
  const effectiveBusinessId =
    embedded && embeddedBusinessId
      ? embeddedBusinessId
      : (routeBusinessId ??
        selectedBusinessId ??
        loggedInUserData?.business?.id);
  const { data: priceListData, refetch: priceListRefetch } =
    useGetPriceListDropdownService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
      ...(effectiveBusinessId ? { businessId: effectiveBusinessId } : {}),
    });
  const { data: businessGroupData, refetch: businessGroupRefetch } =
    useGetBusinessGroupDropdownService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
    });
  const { data: businessData, refetch: businessRefetch } =
    useGetBusinessService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
    });
  const [uploadModal, setUploadModal] = useState<boolean>(false);
  const [imagePreview, setImagePreview] = useState<string>("");
  const priceLists = (priceListData?.pages[0]?.data?.content || []).map(
    (item: { id?: number; title?: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const businessGroups = (businessGroupData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const businesses = (businessData?.pages[0]?.data?.content || []).map(
    (item: { id?: number; title?: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const countries = COUNTRIES.map((item: { value: string; title: string }) => ({
    value: item.value,
    label: item.title,
  }));
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const isArabicLanguage = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    return l === "arabic" || l === "ar" || l.includes("arab");
  };
  const localizedStationNamePlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phStationNameAr") : t("phStationNameEn");
  const times = Array.from({ length: 96 }, (_, i) => {
    const h24 = Math.floor(i / 4);
    const m = (i % 4) * 15;
    const h12 = h24 % 12 || 12;
    const ampm = h24 < 12 ? "AM" : "PM";
    const label = `${h12}${m ? ":" + m.toString().padStart(2, "0") : ""} ${ampm}`;
    const value = `${h24.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
    return { label, value };
  });
  useEffect(() => {
    priceListRefetch();
    businessGroupRefetch();
    if (needsBusinessPicker) businessRefetch();
  }, []);
  useEffect(() => {
    if (needsBusinessPicker && selectedBusinessId) {
      setValue("priceListId", null);
      priceListRefetch();
    }
  }, [selectedBusinessId]);
  useEffect(() => {
    if (stationId && priceListData && businessGroupData) {
      if (loading) getByID();
    }
  }, [params, priceListData, businessGroupData, stationId]);
  useEffect(() => {
    if (isDirty && stationId) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields, stationId]);
  const getByID = () => {
    StationProfileService.getStationByID(stationId!)
      .then(async (res: GetStationByIdResponse) => {
        if (res.success) {
          reset({
            ...(res?.data || {}),
            priceListId: res.data?.priceListId
              ? {
                  value: res.data?.priceListId,
                  label: res?.data?.priceListTitle || res.data?.priceListId,
                }
              : null,
            businessGroupId: res.data?.businessGroupId
              ? {
                  value: res.data?.businessGroupId,
                  label:
                    res?.data?.businessGroupTitle || res?.data?.businessGroupId,
                }
              : null,
            country: res.data?.country
              ? {
                  value: res.data?.country,
                  label: countries.find((v) => v.value === res.data?.country)
                    ?.label,
                }
              : null,
            hours:
              !res?.data?.hours ||
              Object.keys(res?.data?.hours || {}).length === 0
                ? watch("hours")
                : [
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                    "Sunday",
                  ].reduce((acc, day) => {
                    const d = (res.data.hours as any)[day];
                    return {
                      ...acc,
                      [day]: d[0]?.closed
                        ? [{ closed: true }]
                        : d[0]?.open24Hours
                          ? [{ open24Hours: true }]
                          : Array.isArray(d)
                            ? d
                            : [d],
                    };
                  }, {}),
            vehicleRequiredField: {
              ...res.data?.vehicleRequiredField,
              plateNumber: true,
              makeId: true,
              modelId: true,
              vehicleTypeId: true,
              // Removed from vehicle mandatory fields UI — always send false
              odometer: false,
              dailyMileage: false,
              avgDailyFuelConsumption: false,
              seatingCapacity: false,
              fleetCode: false,
              tyreInformation: false,
              notes: false,
              registrationStatus: false,
              registrationExpiryDate: false,
            },
            sellBelowStock: !!res.data?.sellBelowStock,
          });
          const preview = await fetchMediaAsDataUrl(
            (res?.data as any)?.imgUrl || "",
          );
          setImagePreview(preview);
          captureLabel(pickStationDisplayName(res));
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const onSubmit = (data: any, action?: "saveAndClose" | "saveAndNext") => {
    setIsBtnEnabled(false);
    const vehicleRequiredField = {
      ...(data.vehicleRequiredField || {}),
      // Removed from vehicle mandatory fields UI — always send false
      odometer: false,
      dailyMileage: false,
      avgDailyFuelConsumption: false,
      seatingCapacity: false,
      fleetCode: false,
      tyreInformation: false,
      notes: false,
      registrationStatus: false,
      registrationExpiryDate: false,
    };
    if (isBusinessCreateWizard && savedStation?.id && !stationId) {
      setIsBtnEnabled(true);
      if (action === "saveAndClose") {
        navigate(listPath);
      } else {
        // Subscription plan step commented out for now
        // setWizardStep(1);
        navigate(listPath);
      }
      return;
    }
    if (stationId) {
      const imgUrl = data.imgUrl || "";
      let imgFileName = "";
      if (imgUrl) {
        try {
          imgFileName = new URL(imgUrl).pathname.split("/").pop() || "";
        } catch {
          imgFileName = imgUrl.split("?")[0].split("/").pop() || "";
        }
      }
      StationProfileService.updateStation(stationId, {
        ...data,
        vehicleRequiredField,
        imgUrl: imgFileName,
        priceListId: data.priceListId?.value ? data.priceListId?.value : 0,
        businessGroupId: data.businessGroupId?.value
          ? data.businessGroupId?.value
          : 0,
        country: data.country?.value ? data.country?.value : null,
        sellBelowStock: !!data.sellBelowStock,
      })
        .then((response: any) => {
          if (response.success) {
            captureLabel(data?.name || pickStationDisplayName(response));
            setLoading(true);
            getByID();
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("Station") + " " + t("updated") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
          } else {
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        })
        .finally(() => setIsBtnEnabled(true));
    } else {
      StationProfileService.createStation({
        ...data,
        vehicleRequiredField,
        priceListId: data.priceListId?.value ? data.priceListId?.value : 0,
        businessGroupId: data.businessGroupId?.value
          ? data.businessGroupId?.value
          : 0,
        country: data.country?.value ? data.country?.value : null,
        sellBelowStock: !!data.sellBelowStock,
        ...(embedded && embeddedBusinessId
          ? { businessId: Number(embeddedBusinessId) }
          : data.businessId?.value
            ? { businessId: Number(data.businessId.value) }
            : effectiveBusinessId
              ? { businessId: Number(effectiveBusinessId) }
              : {}),
      })
        .then((response: any) => {
          if (response.success) {
            setValue("id", response.data.id);
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("Station") + " " + t("created") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
            if (embedded) {
              if (action === "saveAndClose") {
                onEmbeddedComplete?.();
              } else {
                onEmbeddedComplete?.({
                  id: response.data.id,
                  name: data.name,
                  businessId: embeddedBusinessId || undefined,
                });
              }
            } else if (isBusinessCreateWizard) {
              const created = {
                id: response.data.id,
                name: data.name,
                businessId: Number(effectiveBusinessId) || undefined,
              };
              setSavedStation(created);
              if (action === "saveAndClose") {
                navigate(listPath);
              } else {
                // Subscription plan step commented out for now
                // setWizardStep(1);
                navigate(listPath);
              }
            } else {
              setUploadModal(true);
            }
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setPageNo(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setPageNo(pageNo + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "businessGroups")
        businessGroupRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "priceLists")
        priceListRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "businesses")
        businessRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  return (
    <>
      {!embedded ? (
        <ComponentHeaderTwo
          breadcrumbItems={breadcrumbItems}
          showBtn={true}
          showSaveBtn={!isBusinessCreateWizard}
          onClick={() => {
            // if (isBusinessCreateWizard && wizardStep === 1) {
            //   setWizardStep(0);
            //   return;
            // }
            navigate(listPath);
          }}
          onSubmit={handleSubmit((data) => onSubmit(data))}
          heading={
            // isBusinessCreateWizard && wizardStep === 1
            //   ? t("subscriptionPlan")
            //   :
            stationId ? t("stations") : t("addStations")
          }
          icon={<Station height={28} width={28} />}
          btnText={"back"}
          btnType={"back"}
          isBtnEnabled={IsBtnEnabled}
          showCreateBtn={checkPrivileges("ROLE_Station_CREATE")}
        />
      ) : null}
      {/* {isBusinessCreateWizard ? (
        <Box sx={{ px: 2, pt: 1, pb: 2 }}>
          <Stepper
            activeStep={wizardStep}
            className="reportSteps w-[100%]"
            sx={{
              "& .MuiStepLabel-label": {
                fontFamily: "Saira, sans-serif",
                fontWeight: 600,
                fontSize: '13px',
              },
              "& .MuiStepIcon-root.Mui-active": {
                color: themeColors?.secondary,
              },
              "& .MuiStepIcon-root.Mui-completed": {
                color: themeColors?.secondary,
              },
            }}
          >
            {BUSINESS_STATION_CREATE_STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{t(label)}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      ) : null} */}
      <div className={`${stationId && !embedded ? "flex" : ""}`}>
        {stationId && !embedded ? (
          <StationTabs params={params} selectedIndex={0} />
        ) : null}
        {loading ? (
          <Loader />
        ) : (
          // isBusinessCreateWizard &&
          //   wizardStep === 1 &&
          //   savedStation &&
          //   effectiveBusinessId ? (
          //   <StationSubscriptionPlanStep
          //     stationId={savedStation.id}
          //     stationName={savedStation.name}
          //     businessId={Number(
          //       savedStation.businessId || effectiveBusinessId,
          //     )}
          //     currency={loggedInUserData?.business?.currency}
          //     themeColors={themeColors}
          //     onAssigned={() => navigate(listPath)}
          //     onBack={() => setWizardStep(0)}
          //   />
          // ) :
          <SimpleModal
            visable={true}
            title={
              embedded
                ? t("addStations")
                : isBusinessCreateWizard
                  ? t("addStations")
                  : stationId
                    ? t("stations")
                    : t("addStations")
            }
            isButtonVisable={true}
            isVisableBtn={IsBtnEnabled}
            btnclosetext="Back"
            buttonText={stationId ? "update" : "submit"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showBackButton={embedded}
            backButtonText="back"
            backButtonAction={() => onEmbeddedBack?.()}
            showSecondaryButton={false}
            showPortList={handleSubmit((data) => onSubmit(data))}
            formSubmit={handleSubmit((data) => onSubmit(data))}
            content={
              <>
                <Grid
                  container
                  component="form"
                  id="form"
                  className="items-start"
                  spacing={3}
                  onSubmit={handleSubmit((data) => onSubmit(data))}
                >
                  {needsBusinessPicker ? (
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="businessId"
                        control={control}
                        defaultValue={null}
                        rules={{
                          required: t("This field is required."),
                        }}
                        render={({ field }) => (
                          <DynamicSelect
                            label={t("business") + " *"}
                            {...field}
                            options={businesses}
                            isSearchable
                            isClearable
                            defaultPlaceholder="phSelectBusiness"
                            onChange={(selectedOption: any) => {
                              field.onChange(selectedOption);
                              if (!IsBtnEnabled) setIsBtnEnabled(true);
                            }}
                            error={errors.businessId?.message as string}
                            isLoading={searchLoading === "businesses"}
                            onInputChange={(
                              search: string,
                              { action }: { action: string },
                            ) => {
                              if (
                                action === "input-change" ||
                                (action !== "set-value" &&
                                  action !== "menu-close" &&
                                  action !== "input-blur")
                              )
                                handleDropdownActions(
                                  "businesses",
                                  "searchIt",
                                  search,
                                );
                              else if (
                                action === "input-blur" ||
                                action === "menu-close"
                              ) {
                                if (dropdownSearchTerm || pageNo > 1)
                                  handleDropdownActions(
                                    "businesses",
                                    "searchIt",
                                    "",
                                  );
                                else setDropdownSearchTerm("");
                                setPageNo(1);
                              }
                            }}
                            onMenuScrollToBottom={() => {
                              if (!(businessData?.pages[0]?.data as any)?.last)
                                handleDropdownActions("businesses", "loadNext");
                            }}
                            page={pageNo}
                          />
                        )}
                      />
                    </Grid>
                  ) : null}
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("stationCode", {
                        validate: {
                          required: (value) =>
                            value?.trim() !== "" ||
                            t("This field is required."),
                        },
                      })}
                      label={t("stationCode") + " *"}
                      placeholder={t("phStationCode")}
                      title={t("stationCode")}
                      fullWidth
                      error={!!errors.stationCode}
                      helperText={errors.stationCode?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("name", {
                        validate: {
                          required: (value) =>
                            value?.trim() !== "" ||
                            t("This field is required."),
                        },
                      })}
                      label={`${!primaryLanguage ? t("primary") : ""} ${t("stationName")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`}
                      placeholder={localizedStationNamePlaceholder(
                        primaryLanguage,
                      )}
                      title={`${!primaryLanguage ? t("primary") : ""} ${t("stationName")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`
                        .replace(/\s+/g, " ")
                        .trim()}
                      fullWidth
                      error={!!errors.name}
                      helperText={errors.name?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("secondaryName")}
                      label={`${!secondaryLanguage ? t("secondary") : ""} ${t("stationName")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                      placeholder={localizedStationNamePlaceholder(
                        secondaryLanguage,
                      )}
                      title={`${!secondaryLanguage ? t("secondary") : ""} ${t("stationName")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`
                        .replace(/\s+/g, " ")
                        .trim()}
                      fullWidth
                      error={!!errors.secondaryName}
                      helperText={errors.secondaryName?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      type="number"
                      {...register("numberOfTerminal")}
                      onChange={(e: any) => {
                        const raw = e.target.value;
                        const value =
                          raw !== "" && Number(raw) < 0 ? "0" : raw || "";
                        setValue("numberOfTerminal", value, {
                          shouldDirty: true,
                        });
                        if (!IsBtnEnabled) setIsBtnEnabled(true);
                      }}
                      label={t("numberOfBays")}
                      placeholder={t("phNumberOfBays")}
                      title={t("numberOfBays")}
                      fullWidth
                      error={!!errors.numberOfTerminal}
                      helperText={errors.numberOfTerminal?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="priceListId"
                      control={control}
                      defaultValue={null}
                      rules={{ required: false }}
                      render={({ field }) => (
                        <DynamicSelect
                          label={t("priceList")}
                          {...field}
                          options={priceLists}
                          isSearchable
                          isClearable
                          defaultPlaceholder="phSelectPriceList"
                          onChange={(selectedOption: any) =>
                            field.onChange(selectedOption)
                          }
                          error={errors.priceListId?.message as string}
                          isLoading={searchLoading === "priceLists"}
                          onInputChange={(
                            search: string,
                            { action }: { action: string },
                          ) => {
                            if (
                              action === "input-change" ||
                              (action !== "set-value" &&
                                action !== "menu-close" &&
                                action !== "input-blur")
                            )
                              handleDropdownActions(
                                "priceLists",
                                "searchIt",
                                search,
                              );
                            else if (
                              action === "input-blur" ||
                              action === "menu-close"
                            ) {
                              if (dropdownSearchTerm || pageNo > 1)
                                handleDropdownActions(
                                  "priceLists",
                                  "searchIt",
                                  "",
                                );
                              else setDropdownSearchTerm("");
                              setPageNo(1);
                            }
                          }}
                          onMenuScrollToBottom={() => {
                            if (!(priceListData?.pages[0]?.data as any).last)
                              handleDropdownActions("priceLists", "loadNext");
                          }}
                          page={pageNo}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="businessGroupId"
                      control={control}
                      defaultValue={null}
                      rules={{ required: false }}
                      render={({ field }) => (
                        <DynamicSelect
                          label={t("businessGroup")}
                          {...field}
                          options={businessGroups}
                          isSearchable
                          isClearable
                          defaultPlaceholder="phSelectBusinessGroup"
                          onChange={(selectedOption: any) =>
                            field.onChange(selectedOption)
                          }
                          error={errors.businessGroupId?.message as string}
                          isLoading={searchLoading === "businessGroups"}
                          onInputChange={(
                            search: string,
                            { action }: { action: string },
                          ) => {
                            if (
                              action === "input-change" ||
                              (action !== "set-value" &&
                                action !== "menu-close" &&
                                action !== "input-blur")
                            )
                              handleDropdownActions(
                                "businessGroups",
                                "searchIt",
                                search,
                              );
                            else if (
                              action === "input-blur" ||
                              action === "menu-close"
                            ) {
                              if (dropdownSearchTerm || pageNo > 1)
                                handleDropdownActions(
                                  "businessGroups",
                                  "searchIt",
                                  "",
                                );
                              else setDropdownSearchTerm("");
                              setPageNo(1);
                            }
                          }}
                          onMenuScrollToBottom={() => {
                            if (
                              !(businessGroupData?.pages[0]?.data as any).last
                            )
                              handleDropdownActions(
                                "businessGroups",
                                "loadNext",
                              );
                          }}
                          page={pageNo}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      type="number"
                      {...register("target", {
                        required: t("This field is required."),
                      })}
                      onChange={(e: any) => {
                        const raw = e.target.value;
                        const value =
                          raw !== "" && Number(raw) < 0 ? "0" : raw || "";
                        setValue("target", value, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        if (!IsBtnEnabled) setIsBtnEnabled(true);
                      }}
                      label={t("monthlySalesTarget") + " *"}
                      placeholder={t("phMonthlySalesTarget")}
                      title={t("monthlySalesTarget")}
                      fullWidth
                      error={!!errors.target}
                      helperText={errors.target?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("taxNumber")}
                      label={t("taxNumber")}
                      placeholder={t("phStationTaxNumber")}
                      title={t("taxNumber")}
                      fullWidth
                      error={!!errors.taxNumber}
                      helperText={errors.taxNumber?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      type="text"
                      {...register("phoneNumber1", {
                        required: t("This field is required."),
                        validate: {
                          phoneLength: (value) =>
                            (value?.length >= 5 && value?.length <= 17) ||
                            t("phoneLength"),
                        },
                      })}
                      label={t("phoneNumber1") + " *"}
                      placeholder={t("phPhoneNumber1")}
                      title={t("phoneNumber1")}
                      fullWidth
                      error={!!errors.phoneNumber1}
                      helperText={errors.phoneNumber1?.message as string}
                      InputLabelProps={{ shrink: true }}
                      onChange={(
                        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                      ) => {
                        const cleaned = e.target.value.replace(/[^0-9+]/g, "");
                        e.target.value = cleaned;
                        setValue("phoneNumber1", cleaned, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        if (!IsBtnEnabled) setIsBtnEnabled(true);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      type="text"
                      {...register("phoneNumber2", {
                        validate: {
                          phoneLength: (value) =>
                            !value ||
                            (value?.length >= 5 && value?.length <= 17) ||
                            t("phoneLength"),
                        },
                      })}
                      label={t("phoneNumber2")}
                      placeholder={t("phPhoneNumber2")}
                      title={t("phoneNumber2")}
                      fullWidth
                      error={!!errors.phoneNumber2}
                      helperText={errors.phoneNumber2?.message as string}
                      InputLabelProps={{ shrink: true }}
                      onChange={(
                        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                      ) => {
                        const cleaned = e.target.value.replace(/[^0-9+]/g, "");
                        e.target.value = cleaned;
                        setValue("phoneNumber2", cleaned, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        if (!IsBtnEnabled) setIsBtnEnabled(true);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      type="email"
                      {...register("email", {
                        pattern: {
                          value:
                            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                          message: t("validEmail"),
                        },
                      })}
                      placeholder={t("phStationEmail")}
                      label={t("email")}
                      title={t("email")}
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("website")}
                      label={t("website")}
                      placeholder={t("phWebsite")}
                      title={t("website")}
                      fullWidth
                      error={!!errors.website}
                      helperText={errors.website?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("googleListingAddress", {
                        maxLength: {
                          value: 500,
                          message: t("Address must be under 500 characters"),
                        },
                      })}
                      label={t("googleListingAddress")}
                      placeholder={t("phGoogleListingAddress")}
                      title={t("googleListingAddress")}
                      fullWidth
                      error={!!errors.googleListingAddress}
                      helperText={
                        errors.googleListingAddress?.message as string
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  {!stationId || loggedInUserData?.type === -1 ? (
                    <Grid item xs={12} md={3}>
                      <TextField
                        {...register("subscriptionFlexPriceId", {
                          validate: {
                            required: (value) =>
                              String(value || "").trim() !== "" ||
                              t("This field is required."),
                          },
                        })}
                        label={t("subscriptionId") + " *"}
                        placeholder={t("phSubscriptionId")}
                        title={t("subscriptionId")}
                        fullWidth
                        error={!!errors.subscriptionFlexPriceId}
                        helperText={
                          errors.subscriptionFlexPriceId?.message as string
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  ) : null}
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="sellBelowStock"
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <div className="relative">
                          <CommonCheckbox
                            {...field}
                            label={t("Sell Below Stock")}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              field.onChange(event.target.checked);
                              if (!IsBtnEnabled) setIsBtnEnabled(true);
                            }}
                            checked={!!field.value}
                            color={themeColors?.primary}
                          />
                        </div>
                      )}
                    />
                  </Grid>
                  {/* <Grid item xs={12} md={3}>
                    <TextField
                      {...register("description")}
                      label={t("description")}
                      placeholder={t("description")}
                      title={t("description")}
                      fullWidth
                      error={!!errors.description}
                      helperText={errors.description?.message as string}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={1}
                      sx={{
                        "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                          width: "100%",
                          paddingRight: "0px",
                          paddingBottom: 0,
                        },
                        "& .MuiInputBase-inputMultiline": {
                          resize: "vertical",
                          minHeight: "29px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("extraNotes")}
                      label={t("extraNotes")}
                      placeholder={t("extraNotes")}
                      title={t("extraNotes")}
                      fullWidth
                      error={!!errors.extraNotes}
                      helperText={errors.extraNotes?.message as string}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={1}
                      sx={{
                        "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                          width: "100%",
                          paddingRight: "0px",
                          paddingBottom: 0,
                        },
                        "& .MuiInputBase-inputMultiline": {
                          resize: "vertical",
                          minHeight: "29px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="checkTyre"
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <div className="relative">
                          <CommonCheckbox
                            {...field}
                            label={t("Check Tyre")}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>
                            ) => field.onChange(event.target.checked)}
                            checked={field.value}
                            color={themeColors?.primary}
                          />
                        </div>
                      )}
                    />
                  </Grid> */}
                  <Grid item container xs={12} spacing={2} className="my-[0px]">
                    <Accordion
                      defaultExpanded
                      sx={{
                        boxShadow: "none !important",
                        width: "100% !important",
                        padding: "7px 12px 12px 0",
                        borderRadius: "8px !important",
                        borderTop: "4px solid #fff",
                        "&.Mui-expanded": { borderColor: themeColors?.primary },
                        "& .MuiAccordionSummary-root": {
                          minHeight: "unset !important",
                          paddingRight: 0,
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <Box
                            sx={{
                              background: "#E1E8E3",
                              borderRadius: "8px",
                              padding: "6px",
                            }}
                          >
                            <ExpandMoreIcon
                              sx={{
                                stroke: themeColors?.primary,
                                strokeWidth: "0.3",
                              }}
                            />
                          </Box>
                        }
                        aria-controls="address-content"
                        id="address-header"
                        sx={{
                          "& .MuiAccordionSummary-content": {
                            margin: "0 !important",
                          },
                        }}
                      >
                        <h1
                          className="flex font-Saira font-[700] text-[17px]"
                          style={{ color: themeColors?.primary }}
                        >
                          {t("address")}
                        </h1>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid item container spacing={2} xs={12}>
                          <Grid item xs={12} md={3}>
                            <TextField
                              {...register("postalCode")}
                              label={t("postalCode")}
                              placeholder={t("phPostalCode")}
                              title={t("postalCode")}
                              fullWidth
                              error={!!errors.postalCode}
                              helperText={errors.postalCode?.message as string}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              {...register("buildingNumber")}
                              label={t("buildingNumber")}
                              placeholder={t("phBuildingNumber")}
                              title={t("buildingNumber")}
                              fullWidth
                              error={!!errors.buildingNumber}
                              helperText={
                                errors.buildingNumber?.message as string
                              }
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              {...register("street")}
                              label={t("street")}
                              placeholder={t("phStreet")}
                              title={t("street")}
                              fullWidth
                              error={!!errors.street}
                              helperText={errors.street?.message as string}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              {...register("location")}
                              label={t("location")}
                              placeholder={t("phLocation")}
                              title={t("location")}
                              fullWidth
                              error={!!errors.location}
                              helperText={errors.location?.message as string}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              {...register("region")}
                              label={t("region")}
                              placeholder={t("phRegion")}
                              title={t("region")}
                              fullWidth
                              error={!!errors.region}
                              helperText={errors.region?.message as string}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              {...register("city")}
                              label={t("city")}
                              placeholder={t("phCity")}
                              title={t("city")}
                              fullWidth
                              error={!!errors.city}
                              helperText={errors.city?.message as string}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Controller
                              name="country"
                              control={control}
                              defaultValue={null}
                              rules={{ required: false }}
                              render={({ field }) => (
                                <DynamicSelect
                                  label={t("country")}
                                  {...field}
                                  options={countries}
                                  isSearchable
                                  isClearable
                                  defaultPlaceholder="phSelectCountry"
                                  onChange={(selectedOption: any) =>
                                    field.onChange(selectedOption)
                                  }
                                  error={errors.country?.message as string}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}></Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              {...register("address", {
                                maxLength: {
                                  value: 500,
                                  message: t(
                                    "Address must be under 500 characters",
                                  ),
                                },
                              })}
                              label={t("address")}
                              placeholder={t("phStationAddress")}
                              title={t("address")}
                              fullWidth
                              error={!!errors.address}
                              helperText={errors.address?.message as string}
                              InputLabelProps={{ shrink: true }}
                              multiline
                              rows={1}
                              sx={{
                                "& .MuiInputBase-fullWidth.MuiInputBase-multiline":
                                  {
                                    width: "100%",
                                    paddingRight: "0px",
                                    paddingBottom: 0,
                                  },
                                "& .MuiInputBase-inputMultiline": {
                                  resize: "vertical",
                                  minHeight: "29px",
                                },
                              }}
                            />
                          </Grid>
                          {/* <Grid item xs={12} md={3}>
                            <TextField
                              type="number"
                              {...register("latitude")}
                              onChange={(e: any) => {
                                const value = e.target.value;
                                e.target.value = value < 0 ? 0 : value || "";
                                if (!IsBtnEnabled) setIsBtnEnabled(true);
                              }}
                              label={t("latitude")}
                              placeholder={t("latitude")}
                              title={t("latitude")}
                              fullWidth
                              error={!!errors.latitude}
                              helperText={errors.latitude?.message as string}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              type="number"
                              {...register("longitude")}
                              onChange={(e: any) => {
                                const value = e.target.value;
                                e.target.value = value < 0 ? 0 : value || "";
                                if (!IsBtnEnabled) setIsBtnEnabled(true);
                              }}
                              label={t("longitude")}
                              placeholder={t("longitude")}
                              title={t("longitude")}
                              fullWidth
                              error={!!errors.longitude}
                              helperText={errors.longitude?.message as string}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid> */}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                  {/* <Grid container spacing={2} className="my-[0px] ml-[3px]">
                    <Grid item xs={12} className="px-4">
                      <h1
                        className="flex font-Saira font-[700] text-[17px]"
                        style={{ color: themeColors?.primary }}
                      >
                        {t("others")}
                      </h1>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        {...register("termAndCondition")}
                        label={
                          t("Terms & Conditions") + " (" + t("primary") + ")"
                        }
                        placeholder={t("Terms & Conditions") + " (" + t("primary") + ")"}
                        title={t("Terms & Conditions") + " (" + t("primary") + ")"}
                        fullWidth
                        error={!!errors.termAndCondition}
                        helperText={errors.termAndCondition?.message as string}
                        InputLabelProps={{ shrink: true }}
                        multiline
                        rows={3}
                        sx={{
                          "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                            width: "100%",
                            paddingRight: "0px",
                            paddingBottom: 0,
                          },
                          "& .MuiInputBase-inputMultiline": {
                            resize: "vertical",
                            minHeight: "29px",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        {...register("termAndConditionSecondary")}
                        label={
                          t("Terms & Conditions") + " (" + t("secondary") + ")"
                        }
                        placeholder={t("Terms & Conditions") + " (" + t("secondary") + ")"}
                        title={t("Terms & Conditions") + " (" + t("secondary") + ")"}
                        fullWidth
                        error={!!errors.termAndConditionSecondary}
                        helperText={
                          errors.termAndConditionSecondary?.message as string
                        }
                        InputLabelProps={{ shrink: true }}
                        multiline
                        rows={3}
                        sx={{
                          "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                            width: "100%",
                            paddingRight: "0px",
                            paddingBottom: 0,
                          },
                          "& .MuiInputBase-inputMultiline": {
                            resize: "vertical",
                            minHeight: "29px",
                          },
                        }}
                      />
                    </Grid>
                  </Grid> */}
                  <Grid item container xs={12} spacing={2} className="my-[0px]">
                    <Accordion
                      sx={{
                        boxShadow: "none !important",
                        width: "100% !important",
                        padding: "7px 12px 12px 0",
                        borderRadius: "8px !important",
                        borderTop: "4px solid #fff",
                        "&.Mui-expanded": { borderColor: themeColors?.primary },
                        "& .MuiAccordionSummary-root": {
                          minHeight: "unset !important",
                          paddingRight: 0,
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <Box
                            sx={{
                              background: "#E1E8E3",
                              borderRadius: "8px",
                              padding: "6px",
                            }}
                          >
                            <ExpandMoreIcon
                              sx={{
                                stroke: themeColors?.primary,
                                strokeWidth: "0.3",
                              }}
                            />
                          </Box>
                        }
                        aria-controls="address-content"
                        id="address-header"
                        sx={{
                          "& .MuiAccordionSummary-content": {
                            margin: "0 !important",
                          },
                        }}
                      >
                        <h1
                          className="flex font-Saira font-[700] text-[17px]"
                          style={{ color: themeColors?.primary }}
                        >
                          {t("hours")}
                        </h1>
                      </AccordionSummary>
                      <AccordionDetails>
                        {Object.keys(watch("hours")).map((day) => {
                          const dayData = watch(`hours.${day}`);
                          const isClosed = !!dayData[0]?.closed;
                          const is24 = !!dayData[0]?.open24Hours;
                          return (
                            <Grid
                              key={day}
                              item
                              xs={12}
                              className="flex gap-x-2 items-center pt-[3px] pb-[5px] border-b"
                            >
                              <Typography className="font-Saira text-[14px] font-medium text-txt-color w-[100px] text-left italic">
                                {t(day)}
                              </Typography>
                              <Controller
                                name={`hours.${day}[0].closed`}
                                control={control}
                                render={({ field }) => (
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        {...field}
                                        checked={!field.value}
                                        onChange={(e) => {
                                          const isOpen = e.target.checked;
                                          const closed = !isOpen;
                                          field.onChange(closed);
                                          if (closed) {
                                            setValue(`hours.${day}`, [
                                              { closed: true },
                                            ]);
                                          } else {
                                            setValue(`hours.${day}`, [
                                              {
                                                open: "09:00",
                                                close: "17:00",
                                              },
                                            ]);
                                          }
                                        }}
                                        sx={{
                                          "& .MuiSwitch-switchBase.Mui-checked":
                                            {
                                              color: themeColors?.primary,
                                            },
                                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                            {
                                              backgroundColor:
                                                themeColors?.primary,
                                            },
                                          ".MuiSwitch-track": {
                                            height: "140%",
                                            marginTop: "-3px",
                                            borderRadius: "20px",
                                          },
                                          transform: "scale(1.1)",
                                        }}
                                      />
                                    }
                                    label={
                                      <span className="text-[13px] font-semibold font-Saira tracking-wider">
                                        {!isClosed ? "" : t("closed")}
                                      </span>
                                    }
                                    sx={{
                                      columnGap: "5px",
                                      marginRight: "0px",
                                    }}
                                  />
                                )}
                              />
                              {!isClosed &&
                                !is24 &&
                                Array.isArray(dayData) &&
                                dayData.map((shift: any, index: number) => {
                                  const hasOnlyOne = dayData.length === 1;
                                  const hasError = !!(errors as any)?.hours?.[
                                    day
                                  ]?.[index]?.close;
                                  return (
                                    <>
                                      <Chip
                                        clickable
                                        onClick={() =>
                                          setOpenDay(
                                            openDay === `${day}-${index}` &&
                                              !hasError
                                              ? null
                                              : `${day}-${index}`,
                                          )
                                        }
                                        onDelete={
                                          hasOnlyOne
                                            ? undefined
                                            : () => {
                                                const updated = [...dayData];
                                                updated.splice(index, 1);
                                                setValue(
                                                  `hours.${day}`,
                                                  updated,
                                                );
                                                if (!IsBtnEnabled)
                                                  setIsBtnEnabled(true);
                                              }
                                        }
                                        label={
                                          <Box className="flex gap-1">
                                            <span className="font-bold">
                                              {t("opens")}
                                            </span>
                                            <span className="italic">
                                              {
                                                times.find(
                                                  (t) => t.value === shift.open,
                                                )?.label
                                              }
                                            </span>
                                            <span className="font-bold ms-[5px]">
                                              {t("closes")}
                                            </span>
                                            <span className="italic">
                                              {
                                                times.find(
                                                  (t) =>
                                                    t.value === shift.close,
                                                )?.label
                                              }
                                            </span>
                                          </Box>
                                        }
                                        className="font-Saira text-[11px]"
                                        id={`chip-${day}-${index}`}
                                        sx={{
                                          "& svg": {
                                            fill: "#D32F2F !important",
                                          },
                                        }}
                                      ></Chip>
                                      {index === dayData.length - 1 && (
                                        <Tooltip
                                          followCursor
                                          title={t("addHours")}
                                        >
                                          <IconButton
                                            className="bg-[#E1E8E3] p-[3px] rounded-[8px]"
                                            onClick={() => {
                                              if (!IsBtnEnabled)
                                                setIsBtnEnabled(true);
                                              const current =
                                                watch(`hours.${day}`) || [];
                                              setValue(`hours.${day}`, [
                                                ...current,
                                                {
                                                  open: "09:00",
                                                  close: "17:00",
                                                },
                                              ]);
                                            }}
                                          >
                                            <Add
                                              style={{
                                                color: themeColors?.primary,
                                              }}
                                            />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      <Popper
                                        open={openDay === `${day}-${index}`}
                                        anchorEl={document.getElementById(
                                          `chip-${day}-${index}`,
                                        )}
                                        placement="bottom-start"
                                      >
                                        <ClickAwayListener
                                          onClickAway={() => {
                                            if (!hasError) setOpenDay(null);
                                          }}
                                        >
                                          <Box
                                            className="rounded-[8px] py-[10px] ps-[5px] pe-[15px] w-[220px]"
                                            sx={{
                                              background: themeColors?.appBg,
                                            }}
                                          >
                                            <Box className="flex items-center gap-2">
                                              <Typography className="font-Saira text-[13px] font-semibold text-txt-color ms-[10px]">
                                                {t("opens")}
                                              </Typography>
                                              <Controller
                                                name={`hours.${day}.${index}.open`}
                                                control={control}
                                                render={({ field }) => (
                                                  <Box className="w-[120px] mt-[10px]">
                                                    <DynamicSelect
                                                      label=""
                                                      {...field}
                                                      options={times}
                                                      isSearchable={false}
                                                      onChange={(option) => {
                                                        field.onChange(
                                                          option?.value,
                                                        );
                                                        trigger(
                                                          `hours.${day}.${index}.close`,
                                                        );
                                                      }}
                                                      value={
                                                        times.find(
                                                          (t) =>
                                                            t.value ===
                                                            field.value,
                                                        ) || null
                                                      }
                                                    />
                                                  </Box>
                                                )}
                                              />
                                            </Box>
                                            <Box className="flex items-center gap-2">
                                              <Typography className="font-Saira text-[13px] font-semibold text-txt-color ms-[10px]">
                                                {t("closes")}
                                              </Typography>
                                              <Controller
                                                name={`hours.${day}.${index}.close`}
                                                control={control}
                                                rules={{
                                                  validate: (value) => {
                                                    const open = watch(
                                                      `hours.${day}.${index}.open`,
                                                    );
                                                    if (
                                                      open &&
                                                      value &&
                                                      value < open
                                                    )
                                                      return t(
                                                        "mustBeAfterOpeningTime",
                                                      );
                                                    return true;
                                                  },
                                                }}
                                                render={({
                                                  field,
                                                  fieldState,
                                                }) => {
                                                  const openTime = watch(
                                                    `hours.${day}.${index}.open`,
                                                  );
                                                  const filteredOptions =
                                                    times.map((t) => ({
                                                      ...t,
                                                      isDisabled:
                                                        openTime &&
                                                        t.value < openTime,
                                                    }));
                                                  return (
                                                    <Box
                                                      className="w-[120px] mt-[10px]"
                                                      sx={{
                                                        "& .commonSelectError":
                                                          {
                                                            position:
                                                              "absolute",
                                                            left: "10px",
                                                          },
                                                      }}
                                                    >
                                                      <DynamicSelect
                                                        label=""
                                                        {...field}
                                                        options={
                                                          filteredOptions
                                                        }
                                                        isSearchable={false}
                                                        onChange={(option) =>
                                                          field.onChange(
                                                            option?.value,
                                                          )
                                                        }
                                                        value={
                                                          times.find(
                                                            (t) =>
                                                              t.value ===
                                                              field.value,
                                                          ) || null
                                                        }
                                                        error={
                                                          fieldState?.error
                                                            ?.message
                                                        }
                                                      />
                                                    </Box>
                                                  );
                                                }}
                                              />
                                            </Box>
                                          </Box>
                                        </ClickAwayListener>
                                      </Popper>
                                    </>
                                  );
                                })}
                              {!isClosed && (
                                <Controller
                                  name={`hours.${day}.open24Hours`}
                                  control={control}
                                  render={({ field }) => (
                                    <Box
                                      sx={{
                                        marginLeft: "10px",
                                        "& .MuiFormControlLabel-root": {
                                          padding: "0 !important",
                                        },
                                        "& .checkboxerror": {
                                          height: "0 !important",
                                        },
                                      }}
                                    >
                                      <CommonCheckbox
                                        {...field}
                                        alignWithFieldLabel={false}
                                        label={t("open24Hours")}
                                        checked={!!dayData[0]?.open24Hours}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          field.onChange(checked);
                                          if (checked) {
                                            setValue(`hours.${day}`, [
                                              { open24Hours: true },
                                            ]);
                                          } else {
                                            setValue(`hours.${day}`, [
                                              {
                                                open: "09:00",
                                                close: "17:00",
                                              },
                                            ]);
                                          }
                                        }}
                                      />
                                    </Box>
                                  )}
                                />
                              )}
                            </Grid>
                          );
                        })}
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                  <Grid item container xs={12} spacing={2} className="my-[0px]">
                    <Accordion
                      sx={{
                        boxShadow: "none !important",
                        width: "100% !important",
                        padding: "7px 12px 12px 0",
                        borderRadius: "8px !important",
                        borderTop: "4px solid #fff",
                        "&.Mui-expanded": { borderColor: themeColors?.primary },
                        "& .MuiAccordionSummary-root": {
                          minHeight: "unset !important",
                          paddingRight: 0,
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <Box
                            sx={{
                              background: "#E1E8E3",
                              borderRadius: "8px",
                              padding: "6px",
                            }}
                          >
                            <ExpandMoreIcon
                              sx={{
                                stroke: themeColors?.primary,
                                strokeWidth: "0.3",
                              }}
                            />
                          </Box>
                        }
                        aria-controls="address-content"
                        id="address-header"
                        sx={{
                          "& .MuiAccordionSummary-content": {
                            margin: "0 !important",
                          },
                        }}
                      >
                        <h1
                          className="flex font-Saira font-[700] text-[17px]"
                          style={{ color: themeColors?.primary }}
                        >
                          {t("vehicleMandatoryFields")}
                        </h1>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid item container spacing={2} xs={12}>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.plateNumber`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={`${!primaryLanguage ? t("primary") : ""} ${t("plateNumber")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""}`}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                    disabled={true}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.secondaryPlateNumber`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={`${!secondaryLanguage ? t("secondary") : ""} ${t("plateNumber")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.vin`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={t("vin")}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.stateId`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={t("state")}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.vehicleTypeId`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={t("vehicleType")}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                    disabled={true}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.makeId`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={t("makeModel")}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                    disabled={true}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.year`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={t("year")}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.transmission`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={t("transmissionType")}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.fuelType`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={t("fuelType")}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.color`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={t("color")}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          {/* Removed from vehicle mandatory fields section
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.odometer`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={t("odometer")}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.dailyMileage`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={t("avgDailyMileage")}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.avgDailyFuelConsumption`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={t("avgDailyFuelConsumption")}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.seatingCapacity`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={t("seatingCapacity")}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.fleetCode`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={t("fleetCode")}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.registrationStatus`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={t("registrationStatus")}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.registrationExpiryDate`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={t("registrationExpiryDate")}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.tyreInformation`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={t("tyreInformation")}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.notes`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={t("notes")}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          */}
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.insuranceStatus`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={t("insuranceStatus")}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.insuranceExpiryDate`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={t("insuranceExpiryDate")}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Controller
                              name={`vehicleRequiredField.insuranceCompanyId`}
                              control={control}
                              render={({ field }) => (
                                <Box
                                  sx={{
                                    "& label": { paddingTop: "0px !important" },
                                    "& .checkboxerror": {
                                      display: "none !important",
                                    },
                                  }}
                                >
                                  <CommonCheckbox
                                    {...field}
                                    alignWithFieldLabel={false}
                                    label={t("insuranceCompany")}
                                    checked={field.value}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => field.onChange(e.target.checked)}
                                    color={themeColors?.primary}
                                  />
                                </Box>
                              )}
                            />
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                </Grid>
                {stationId ? (
                  <Grid container rowSpacing={2} className="my-[0px] ml-[3px]">
                    <Grid item xs={12} md={4} lg={3}>
                      <Typography className="font-Saira font-[600] text-[13px] text-txt-color text-start">
                        {t("Upload") + " " + t("station") + " " + t("logo")}
                      </Typography>
                      <StationMedia
                        formData={getValues()}
                        method="update"
                        callback={async (url: string) => {
                          setValue("imgUrl", url);
                          const preview = await fetchMediaAsDataUrl(url || "");
                          setImagePreview(preview);
                        }}
                        setIsBtnEnabled={setIsBtnEnabled}
                      />
                      <MediaImage src={imagePreview} />
                    </Grid>
                  </Grid>
                ) : null}
                {uploadModal && !embedded && (
                  <FormDialog
                    open={uploadModal}
                    onClose={(event: any, reason: any) => {
                      if (event && reason !== "backdropClick") {
                        navigate(listPath);
                      }
                    }}
                    title={t("Upload") + " " + t("station") + " " + t("logo")}
                    createPopup={true}
                    hideActions={true}
                    content={
                      <StationMedia formData={getValues()} method="create" />
                    }
                  />
                )}
              </>
            }
          />
        )}
      </div>
    </>
  );
}
